/**
 * 🔒 PROTECTED GUEST-FACING MODULE — DO NOT MODIFY WITHOUT EXPLICIT USER APPROVAL
 * ═══════════════════════════════════════════════════════════════════════════════
 * Locked under /app/memory/PROTECTED_GUEST_FACING.md (Feb 2026).
 * Read that file before proposing edits.
 * Locked behavior: Where venue image height = 240px (NOT 150).
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * TheEveningCard — accordion sections: WHEN / WHERE / ATTIRE
 * Each header is tappable; content slides down smoothly on tap.
 * All fields from guest_site_config → event_details.
 */

import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Shirt, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const darkGlass = {
  background: 'rgba(20,15,8,0.58)',
  backdropFilter: 'blur(18px) saturate(1.20)',
  WebkitBackdropFilter: 'blur(18px) saturate(1.20)',
  border: '1.5px solid rgba(255,255,255,0.18)',
  boxShadow: [
    'inset 0 2px 0 rgba(255,255,255,0.14)',
    'inset 0 -1px 0 rgba(212,168,83,0.30)',
    '0 24px 48px rgba(0,0,0,0.28)',
    '0 6px 16px rgba(0,0,0,0.16)',
  ].join(', '),
};

const gold = { color: '#D4AF37', textShadow: '0 1px 0 rgba(0,0,0,0.30)' };

const DEFAULTS = {
  day: 'Saturday', date: 'July 25, 2026',
  venue_name: "Season's Catering",
  venue_address: '644 Pascack Rd, Township of Washington, NJ 07676',
  attire_note_men: 'Suit & Tie', attire_note_women: 'Cocktail or Gown',
  venue_photo_url: '',
};

// ─── Accordion section wrapper ─────────────────────────────────────────────────
function AccordionSection({ icon: Icon, label, isOpen, onToggle, children }) {
  return (
    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      {/* Tappable header */}
      <button
        onClick={onToggle}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={16} color="rgba(212,175,55,0.80)" />
          <span style={{ fontFamily: "'Cinzel',serif", fontSize: 16, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '3px', ...gold }}>
            {label}
          </span>
        </div>
        <ChevronDown size={16} color="rgba(212,175,55,0.65)"
          style={{ transition: 'transform 0.40s ease', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>

      {/* Accordion content — smooth slide */}
      <div style={{
        overflow: 'hidden',
        maxHeight: isOpen ? '600px' : '0px',
        transition: 'max-height 0.80s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <div style={{ padding: '4px 22px 22px' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const VENUE_PHOTOS = [
  { src: 'https://customer-assets.emergentagent.com/job_b4805a07-765d-4b82-bd36-89754495be40/artifacts/j6ndejxo_seasons%20front.jpg', label: 'Exterior' },
  { src: 'https://customer-assets.emergentagent.com/job_b4805a07-765d-4b82-bd36-89754495be40/artifacts/0dfqqamu_Clubnew.jpg',         label: 'Ballroom'  },
];

function VenuePhotoCarousel() {
  const [idx, setIdx]         = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const cycle = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % VENUE_PHOTOS.length);
        setVisible(true);
      }, 600);
    }, 4500);
    return () => clearInterval(cycle);
  }, []);

  const photo = VENUE_PHOTOS[idx];

  return (
    <div style={{ width: '100%', height: 240, borderRadius: 14, overflow: 'hidden',
      marginBottom: 14, position: 'relative',
      background: 'rgba(20,15,8,0.80)' }}>
      <img
        src={photo.src}
        alt={photo.label}
        style={{
          width: '100%', height: '100%', objectFit: 'cover',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.60s ease',
        }}
      />
      {/* Bottom gradient */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 52,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.65))', pointerEvents: 'none' }} />
      {/* Label */}
      <p style={{ position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center',
        fontFamily: "'Cinzel',serif", fontSize: 9, fontWeight: 700, letterSpacing: '2px',
        color: 'rgba(255,255,255,0.88)', margin: 0, textTransform: 'uppercase',
        opacity: visible ? 1 : 0, transition: 'opacity 0.60s ease' }}>
        {photo.label}
      </p>
      {/* Dot indicators */}
      <div style={{ position: 'absolute', top: 8, right: 10,
        display: 'flex', gap: 4, alignItems: 'center' }}>
        {VENUE_PHOTOS.map((_, i) => (
          <div key={i} style={{
            width: i === idx ? 14 : 5, height: 5, borderRadius: 3,
            background: i === idx ? 'rgba(255,255,255,0.90)' : 'rgba(255,255,255,0.40)',
            transition: 'all 0.4s ease',
          }} />
        ))}
      </div>
    </div>
  );
}

const TheEveningCard = () => {
  const [details, setDetails] = useState(DEFAULTS);
  const [openSection, setOpenSection] = useState('when'); // controlled: only one open at a time

  useEffect(() => {
    fetch(`${API}/api/public/guest-site-config`)
      .then(r => r.json())
      .then(d => { if (d?.event_details) setDetails(prev => ({ ...prev, ...d.event_details })); })
      .catch(() => {});
  }, []);

  const toggle = (key) => setOpenSection(prev => prev === key ? null : key);
  const mapsUrl = (addr) => `https://maps.google.com/?q=${encodeURIComponent(addr)}`;

  return (
    <div className="card-h-pad" style={{ width: '100%', paddingTop: 0, paddingBottom: 0, boxSizing: 'border-box' }}>
      <div style={{ ...darkGlass, borderRadius: 20, position: 'relative', overflow: 'hidden' }}>

        {/* Gold top bar */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, pointerEvents: 'none',
          background: 'linear-gradient(90deg,transparent 0%,rgba(180,140,30,0.55) 15%,rgba(220,185,60,0.90) 50%,rgba(180,140,30,0.55) 85%,transparent 100%)' }} />
        <div style={{ position: 'absolute', top: 2, left: 0, right: 0, height: 1, pointerEvents: 'none',
          background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.80) 30%,rgba(255,255,255,0.80) 70%,transparent)' }} />

        {/* ── WHEN ── */}
        <AccordionSection icon={Clock} label="When" isOpen={openSection === 'when'} onToggle={() => toggle('when')}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontFamily: "'Cinzel',serif", fontSize: 24, fontWeight: 600,
              color: 'rgba(255,255,255,0.97)', margin: '0 0 6px', letterSpacing: '1.5px' }}>
              {details.day}
            </p>
            <p style={{ fontFamily: "'Cinzel',serif", fontSize: 20, fontWeight: 700,
              color: 'rgba(255,255,255,0.97)', margin: '0 0 12px', letterSpacing: '2px' }}>
              {details.date}
            </p>
            {/* Time — Cormorant Garamond for elegance, split so numbers dominate PM */}
            <div style={{ display:'flex', alignItems:'baseline', justifyContent:'center',
              gap:4, marginBottom:8 }}>
              <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:30, fontWeight:600,
                color:'rgba(255,255,255,0.97)', lineHeight:1, letterSpacing:'1px' }}>12:00</span>
              <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:600,
                color:'rgba(255,255,255,0.97)', letterSpacing:'2px', paddingBottom:2 }}>PM</span>
            </div>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontStyle:'italic',
              color:'rgba(255,255,255,0.97)', margin:0 }}>
              (Aim to arrive by 11:45 AM)
            </p>
          </div>
        </AccordionSection>

        {/* ── WHERE ── */}
        <AccordionSection icon={MapPin} label="Where" isOpen={openSection === 'where'} onToggle={() => toggle('where')}>
          <div>
            {/* Venue photos — crossfade carousel */}
            <VenuePhotoCarousel />

            {/* Venue name on dark strip */}
            <div style={{ background: 'rgba(28,22,10,0.72)', borderRadius: 12, padding: '12px 16px',
              marginBottom: 8, textAlign: 'center' }}>
              <p style={{ fontFamily: "'Cinzel',serif", fontSize: 15, fontWeight: 700,
                color: 'rgba(255,255,255,0.97)', margin: 0, letterSpacing: '0.5px' }}>
                Season's Catering
              </p>
            </div>

            {/* Address — gold pin as map link */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 6, padding: '4px 0' }}>
              <button onClick={() => window.open(mapsUrl(details.venue_address), '_blank')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <MapPin size={13} color="rgba(212,175,55,0.80)" style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 600,
                  color: 'rgba(255,255,255,0.97)', textAlign: 'left', letterSpacing: '0.2px', lineHeight: 1.5 }}>
                  {details.venue_address}
                </span>
              </button>
            </div>
          </div>
        </AccordionSection>

        {/* ── ATTIRE ── */}
        <AccordionSection icon={Shirt} label="Attire" isOpen={openSection === 'attire'} onToggle={() => toggle('attire')}>
          <div>
            <p style={{ fontFamily: "'Cinzel',serif", fontSize: 18, fontWeight: 600, textAlign: 'center',
              ...gold, margin: '0 0 14px', letterSpacing: '2px', textTransform: 'uppercase' }}>
              Formal
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {/* Men */}
              <div style={{ borderRadius: 16, border: '1px solid rgba(212,175,55,0.22)',
                background: 'rgba(255,255,255,0.05)', padding: '10px 6px 12px', textAlign: 'center',
                display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '88%', height: 180, position: 'relative', marginBottom: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ position: 'absolute', inset: 0, borderRadius: 8, pointerEvents: 'none',
                    background: 'radial-gradient(ellipse at 50% 60%, rgba(255,248,210,0.45) 0%, rgba(255,230,140,0.18) 45%, transparent 72%)' }} />
                  <img src="/formal_mens_suit.png" alt="Men's formal attire"
                    style={{ width: '100%', height: '100%', objectFit: 'contain',
                      position: 'relative', zIndex: 1,
                      filter: 'drop-shadow(0 0 18px rgba(255,240,160,0.35)) drop-shadow(0 4px 12px rgba(0,0,0,0.45))' }} />
                </div>
                <p style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '1.5px',
                  color: 'rgba(212,175,55,0.85)', margin: '0 0 4px' }}>Gentlemen</p>
                <p style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 500,
                  color: 'rgba(255,255,255,0.97)', margin: 0 }}>Suit &amp; Tie</p>
              </div>

              {/* Women */}
              <div style={{ borderRadius: 16, border: '1px solid rgba(212,175,55,0.22)',
                background: 'rgba(255,255,255,0.05)', padding: '10px 6px 12px', textAlign: 'center',
                display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '88%', height: 180, position: 'relative', marginBottom: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ position: 'absolute', inset: 0, borderRadius: 8, pointerEvents: 'none',
                    background: 'radial-gradient(ellipse at 50% 60%, rgba(255,248,210,0.45) 0%, rgba(255,230,140,0.18) 45%, transparent 72%)' }} />
                  <img src="/formal_womens_gown.png" alt="Women's formal attire"
                    style={{ width: '100%', height: '100%', objectFit: 'contain',
                      position: 'relative', zIndex: 1,
                      filter: 'drop-shadow(0 0 18px rgba(255,240,160,0.35)) drop-shadow(0 4px 12px rgba(0,0,0,0.45))' }} />
                </div>
                <p style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '1.5px',
                  color: 'rgba(212,175,55,0.85)', margin: '0 0 4px' }}>Ladies</p>
                <p style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 500,
                  color: 'rgba(255,255,255,0.97)', margin: 0 }}>Cocktail or Gown</p>
              </div>
            </div>
          </div>
        </AccordionSection>

        {/* Gold bottom edge */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, pointerEvents: 'none',
          background: 'linear-gradient(90deg,transparent 5%,rgba(212,175,55,0.35) 30%,rgba(212,175,55,0.35) 70%,transparent 95%)' }} />
      </div>
    </div>
  );
};

export default TheEveningCard;
